import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { getVisibleDate } from '../../../../../common/src/date_string.ts';
import { IntervalSongList } from '../../../../../common/src/model.ts';
import ExpandIcon from '../../../assets/expand.svg?react';
import { LocalStorageKey } from '../../../data/client_local_storage.ts';
import { useLocalStorage } from '../../../data/use_local_storage.tsx';
import { getPlatform, Platform } from '../../../util/platform.ts';
import { getTermTitle } from '../../../util/school.ts';
import { Menu, MenuEntry, PlannerHeaderButtonGap, YearHeader } from './shared.tsx';
import { EditTermsButton } from './term_planner.tsx';

interface Props {
  term: IntervalSongList | undefined;
  onSelectTerm: (term: IntervalSongList) => void;
}

export function TermSelector({ term, onSelectTerm }: Props) {
  const [schoolTerms] = useLocalStorage(LocalStorageKey.SchoolTerms);
  const [menuOpen, setMenuOpen] = useState(false);

  const onClickTermSelector = useCallback(() => {
    setMenuOpen(!menuOpen);
  }, [menuOpen]);

  const onBlur = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <TermSelectorWrapper>
      <MenuPositioner>
        <TermSelectorInnerWrapper tabIndex={1000} onClick={onClickTermSelector} onBlur={onBlur}>
          <CurrentTerm>
            <YearHeader>{getTitle(term)}</YearHeader>
            <ExpandIconWrapper />
          </CurrentTerm>
          {menuOpen && (
            <Menu>
              {[...schoolTerms.toReversed().entries()].map(([index, term]) => (
                <TermMenuEntry
                  key={term.name + term.interval.start}
                  isFirst={index === 0}
                  term={term}
                  setSelectedTerm={onSelectTerm}
                />
              ))}
              {getPlatform() !== Platform.Mobile && (
                <MenuEntry $isFirst={false} $isLast={true} $borderTop={true}>
                  <MenuEntryStyle>
                    <EditTermsButton styled={false} />
                  </MenuEntryStyle>
                </MenuEntry>
              )}
            </Menu>
          )}
        </TermSelectorInnerWrapper>
      </MenuPositioner>
    </TermSelectorWrapper>
  );
}

const TermSelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: ${PlannerHeaderButtonGap};
`;

const MenuPositioner = styled.div`
  position: relative;
`;

const TermSelectorInnerWrapper = styled.div`
  cursor: pointer;
`;

const ExpandIconWrapper = styled(ExpandIcon)`
  width: 20px;
  height: 20px;
  fill: var(--color-text);
`;

function getTitle(selectedTerm: IntervalSongList | undefined): string {
  if (!selectedTerm) {
    return 'School Music Planner';
  }
  return `${getTermTitle(selectedTerm)} Term`;
}

const TermWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 15px;
`;

function TermMenuEntry({
  isFirst,
  term,
  setSelectedTerm,
}: {
  isFirst: boolean;
  term: IntervalSongList;
  setSelectedTerm: (term: IntervalSongList) => void;
}) {
  const onClick = useCallback(() => {
    setSelectedTerm(term);
  }, [setSelectedTerm, term]);
  return (
    <MenuEntry $isFirst={isFirst} $isLast={false} onClick={onClick}>
      <MenuEntryStyle>
        <TermWrapper>
          <div key="name">{term.name}</div>
          <div key="start-date">{getVisibleDate(term.interval.start)}</div>
        </TermWrapper>
      </MenuEntryStyle>
    </MenuEntry>
  );
}

const MenuEntryStyle = styled(YearHeader)`
  font-size: 1.3rem;
  width: 100%;
  background-color: transparent;
`;

const CurrentTerm = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;
